import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { Person } from '@/types/leads';
import type { AxiosError } from 'axios';

export interface CreatePersonPayload {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  channel: 'EMAIL' | 'WHATSAPP';
}

export interface UpdatePersonPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface PersonMutationResult {
  person: Person;
  existing: boolean;
  merged: boolean;
}

interface ApiErrorResponse {
  detail?: string;
  firstName?: string[];
  lastName?: string[];
  email?: string[];
  phoneNumber?: string[];
  [key: string]: string[] | string | undefined;
}

export const useCreatePerson = () => {
  const queryClient = useQueryClient();
  return useMutation<PersonMutationResult, AxiosError<ApiErrorResponse>, CreatePersonPayload>({
    mutationFn: async (payload) => {
      try {
        const { data } = await api.post<{ person: Person }>(API_ROUTES.persons, payload);
        return { person: data.person, existing: false, merged: false };
      } catch (err) {
        const axiosErr = err as AxiosError<ApiErrorResponse>;
        if (axiosErr.response?.status === 409) {
          const body = axiosErr.response.data as unknown as {
            detail: string;
            person: Person;
            merged?: boolean;
          };
          return { person: body.person, existing: true, merged: Boolean(body.merged) };
        }
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
  });
};

export const useUpdatePerson = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Person,
    AxiosError<ApiErrorResponse>,
    { id: number; payload: UpdatePersonPayload }
  >({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.patch<Person>(`${API_ROUTES.persons}${id}/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
  });
};
